<template>
  <div class="rental-detail-page">
    <!-- ===== 面包屑 ===== -->
    <div class="breadcrumb">
      <div class="breadcrumb-container">
        <router-link to="/">{{ $t("detail.breadcrumbHome") }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <router-link to="/rental">{{
          $t("detail.breadcrumbRental")
        }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current"
          >{{ $t("s_model") }} {{ $t("detail.breadcrumbDetail") }}</span
        >
      </div>
    </div>

    <!-- ===== 主内容 ===== -->
    <section class="detail-main">
      <div class="detail-container">
        <!-- 服务器名称 & 标签 -->
        <div class="detail-header">
          <div class="detail-title-group">
            <h1 class="detail-title">{{ $t("s_model") }}</h1>
            <!-- <span class="detail-spec">{{ ServerSpec[server.spec] }}</span> -->
          </div>
          <div class="detail-badges">
            <span v-if="server.highlight == 2" class="badge badge-hot">{{
              $t("detail.badgeHot")
            }}</span>
            <span class="badge" :class="getServerStatusClass(server)">
              {{ getServerStatusText(server) }}
            </span>
          </div>
          <div class="detail-badges">
            <span class="xxqq">{{ $t("detail.code") }} {{ $t("s_code") }}</span>
          </div>
        </div>
      </div>

      <div class="detail-container">
        <!-- 左侧：服务器信息 -->
        <div class="detail-left">
          <!-- GPU 信息 -->
          <div class="detail-gpu-card">
            <ServerIconIsometric size="sm" />

            <div class="gpu-info" style="max-width: 87%">
              <div class="gpu-model">{{ truncateText($t("s_core"), 60) }}</div>
              <!-- <div class="gpu-details">
                <span class="gpu-detail-item">{{ $t("s_ram") }}</span>
                <span class="gpu-dot">·</span>
                <span class="gpu-detail-item">{{ $t("s_vram") }}</span>
                <span class="gpu-dot">·</span>
                <span class="gpu-detail-item">{{
                  truncateText($t("s_ssd"), 20)
                }}</span>
              </div> -->
            </div>
          </div>

          <!-- ===== 完整规格参数 + 收益趋势图（新布局） ===== -->
          <div class="detail-specs-wrapper">
            <!-- 规格参数卡片 -->
            <div class="detail-specs-card">
              <div class="card-header">
                <span class="card-icon">⚙️</span>
                <h3 class="card-title">{{ $t("detail.specTitle") }}</h3>
              </div>
              <div class="specs-grid">
                <div class="spec-item">
                  <span class="spec-label">{{ $t("detail.specGpu") }}</span>
                  <span class="spec-value">{{ $t("s_core") }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ $t("detail.specVram") }}</span>
                  <span class="spec-value">{{ $t("s_vram") }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ $t("detail.specStorage") }}</span>
                  <span class="spec-value">{{ $t("s_ssd") }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ $t("detail.specRam") }}</span>
                  <span class="spec-value">{{ $t("s_ram") }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{
                    $t("detail.specBandwidth")
                  }}</span>
                  <span class="spec-value">{{ $t("s_bandwidth") }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ $t("detail.specValue") }}</span>
                  <span class="spec-value">${{ $t("s_value") }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ $t("detail.specCountry") }}</span>
                  <span class="spec-value">
                    <img
                      v-if="server.country_img"
                      :src="server.country_img"
                      :alt="$t('detail.specCountryFlag')"
                      style="width: 24px"
                    />
                    {{ $t("s_country") }}/{{ $t("s_region") }}
                  </span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ $t("detail.specStatus") }}</span>
                  <span
                    class="spec-value"
                    :class="getServerStatusClass(server)"
                  >
                    {{ getServerStatusText(server) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- 收益趋势图卡片 -->
            <div class="detail-chart-card">
              <div class="card-header">
                <span class="card-icon">📈</span>
                <h3 class="card-title">{{ $t("detail.chartTitle") }}</h3>
                <span class="chart-badge">{{
                  $t("detail.chartRealTime")
                }}</span>
              </div>
              <div class="chart-preview" @click="openChartModal">
                <div ref="incomeChart" class="chart-container"></div>
                <div class="chart-hint">
                  <span>{{ $t("detail.chartHint") }}</span>
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="currentColor"
                  >
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                </div>
              </div>
              <!-- 快捷筛选 -->
              <div class="chart-filter">
                <button
                  class="filter-btn"
                  :class="{ active: chartRange === 7 }"
                  @click="switchChartRange(7)"
                >
                  {{ $t("detail.chart7d") }}
                </button>
                <button
                  class="filter-btn"
                  :class="{ active: chartRange === 15 }"
                  @click="switchChartRange(15)"
                >
                  {{ $t("detail.chart15d") }}
                </button>
                <button
                  class="filter-btn"
                  :class="{ active: chartRange === 30 }"
                  @click="switchChartRange(30)"
                >
                  {{ $t("detail.chart30d") }}
                </button>
              </div>
              <!-- 统计摘要 -->
              <div class="chart-stats">
                <div class="chart-stat-item">
                  <span class="stat-label">{{ $t("detail.statMax") }}</span>
                  <span class="stat-value stat-high"
                    >${{ chartStats.max }}</span
                  >
                </div>
                <div class="chart-stat-divider"></div>
                <div class="chart-stat-item">
                  <span class="stat-label">{{ $t("detail.statAvg") }}</span>
                  <span class="stat-value stat-avg">${{ chartStats.avg }}</span>
                </div>
                <div class="chart-stat-divider"></div>
                <div class="chart-stat-item">
                  <span class="stat-label">{{ $t("detail.statMin") }}</span>
                  <span class="stat-value stat-low">${{ chartStats.min }}</span>
                </div>
                <div class="chart-stat-divider"></div>
                <div class="chart-stat-item">
                  <span class="stat-label">{{ $t("detail.statChange") }}</span>
                  <span class="stat-value stat-change"
                    >{{ chartStats.change }}%</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- 服务器描述 -->
          <div class="detail-description">
            <div class="desc-header">
              <span class="desc-icon">📄</span>
              <h3 class="desc-title">{{ $t("detail.descProduct") }}</h3>
            </div>
            <p v-html="$t('s_use_desc')"></p>
          </div>

          <!-- 国家/地区 -->
          <!-- <div class="detail-scenarios">
            <div class="scenarios-header">
              <span class="scenarios-icon">📍</span>
              <h3 class="scenarios-title">国家/地区</h3>
            </div>
            <div class="scenarios-tags">
              <span class="scenario-tag">
                {{ "亚太地区" }}
              </span>
              <span class="scenario-tag">
                <img
                  v-if="server.country_img"
                  :src="server.country_img"
                  alt=""
                  class="country-flag-small"
                />
                {{ server.country_name }}
              </span>
              <span class="scenario-tag">
                {{ "贵州省" }}
              </span>
            </div>
          </div> -->

          <!-- 服务器描述 -->
          <div class="detail-description" style="margin-top: 15px">
            <div class="desc-header">
              <span class="desc-icon">📄</span>
              <h3 class="desc-title">{{ $t("detail.descRegion") }}</h3>
            </div>
            <p v-html="$t('s_features')"></p>
          </div>
        </div>

        <!-- ===== 右侧：租赁面板 ===== -->
        <div class="detail-right">
          <div class="rental-panel">
            <div class="panel-header">
              <span class="panel-title">{{ $t("detail.rentNow") }}</span>
              <span
                class="panel-status badge"
                :class="getServerStatusClass(server)"
              >
                {{ getServerStatusText(server) }}
              </span>
            </div>

            <!-- ===== 1. 活动周期 ===== -->
            <div class="panel-group">
              <label class="panel-label">{{
                $t("detail.activityPeriod")
              }}</label>
              <div class="activity-period-display">
                <div class="period-date-group">
                  <span class="period-display-value">{{ formattedStart }}</span>
                  <span class="period-display-arrow">→</span>
                  <span class="period-display-value">{{ formattedEnd }}</span>
                </div>
                <div class="period-display-days">
                  <span class="days-label">{{ $t("detail.daysLeft") }}</span>
                  <span class="days-number">{{ activityDays }}</span>
                  <span class="days-label">{{ $t("detail.days") }}</span>
                </div>
              </div>
            </div>

            <!-- ===== 2. 认购滑条 ===== -->
            <div class="panel-group">
              <label class="panel-label">{{
                $t("detail.subscribeShare")
              }}</label>
              <div class="slider-wrapper">
                <input
                  type="range"
                  v-model="subscribePercent"
                  min="1"
                  max="100"
                  step="1"
                  class="subscribe-slider"
                  @input="onSliderInput"
                />
                <div class="slider-labels">
                  <span>1%</span>
                  <span class="current-value">{{ subscribePercent }}%</span>
                  <span>100%</span>
                </div>
              </div>

              <!-- ===== 新增：百分比输入框 ===== -->
              <div class="panel-group">
                <label class="panel-label">{{
                  $t("detail.customShare")
                }}</label>
                <span class="remaining-share">
                  {{ $t("detail.remainingShare") }}：{{ remainingPercent }}%
                </span>
                <div class="percent-input-group">
                  <input
                    type="number"
                    v-model="inputPercent"
                    min="1"
                    :max="remainingPercent"
                    step="0.01"
                    class="percent-input"
                    @input="onPercentInput"
                  />

                  <span class="percent-unit">%</span>
                </div>
              </div>
            </div>

            <!-- ===== 3. 预估收益明细 ===== -->
            <div
              class="panel-total stage-wrapper"
              @mouseenter="expandStages = true"
              @mouseleave="expandStages = false"
            >
              <div class="total-header">
                <span class="total-title"
                  >{{ $t("detail.incomeDetail") }}
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="$t('detail.incomeTip')"
                    placement="top"
                  >
                    <i class="el-icon-question xxb"></i>
                  </el-tooltip>
                </span>
                <span class="total-badge">{{ $t("detail.estimate") }}</span>
              </div>

              <!-- 阶段收益展示 -->
              <div
                class="total-details stage-timeline"
                :class="{ expanded: expandStages }"
              >
                <!-- 默认全部隐藏，悬停展开显示 -->
                <div
                  class="total-row stage-item"
                  v-for="(stage, index) in stageIncomeList"
                  :key="index"
                  :class="{ hidden: !expandStages }"
                >
                  <span class="row-label">
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="'收益率:' + (stage.rate * 100).toFixed(2) + '%'"
                      placement="top"
                    >
                      <span class="row-icon"> 📅 </span>
                    </el-tooltip>
                    {{ index + 1 + $t("phase") }}
                    <span class="stage-date"
                      >{{ $t("detail.settleDate") }}{{ stage.end_time }}</span
                    >
                  </span>
                  <span class="row-value stage-income"
                    >≈ ${{ stage.income }}</span
                  >
                </div>

                <!-- ===== 折叠状态下的"更多"提示 ===== -->
                <div
                  class="stage-more-hint"
                  v-if="!expandStages && stageIncomeList.length > 0"
                >
                  <span class="more-dots">···</span>
                  <span class="more-text">{{
                    $t("detail.moreStages", { count: stageIncomeList.length })
                  }}</span>
                  <span class="more-arrow">{{ $t("detail.hoverExpand") }}</span>
                </div>

                <div class="total-divider"></div>

                <div class="total-row total-final">
                  <span class="row-label">
                    <span class="row-icon">🏷️</span>
                    {{ $t("detail.totalIncome") }}
                  </span>
                  <span class="total-price income"
                    >≈ ${{ stageTotalIncome }}</span
                  >
                </div>
              </div>
            </div>

            <!-- ===== 4. 结算 ===== -->
            <div class="panel-total">
              <div class="total-header">
                <span class="total-title">{{ $t("detail.fundDetail") }}</span>
              </div>

              <!-- 明细列表 -->
              <div class="total-details">
                <!-- 认购金额 -->
                <div class="total-row">
                  <span class="row-label">
                    <span class="row-icon">📊</span>
                    {{ $t("detail.rentAmount") }}
                  </span>
                  <span class="row-value">${{ totalPrice }}</span>
                </div>

                <!-- 服务费（如有） -->
                <div class="total-row service-row" v-if="serviceFee > 0">
                  <span class="row-label">
                    <span class="row-icon">🛡️</span>
                    {{ $t("detail.maintenanceFee") }}
                  </span>
                  <div style="display: flex; align-items: center; gap: 4px">
                    <span class="row-value service-value"
                      >${{ serviceFee }}</span
                    >
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="
                        '总价值 * ' +
                        (server.buy_fee * 100).toFixed(2) +
                        '% * ' +
                        subscribePercent +
                        '%'
                      "
                      placement="top"
                    >
                      <i class="el-icon-question xxb"></i>
                    </el-tooltip>
                  </div>
                </div>

                <!-- 优惠（如有） -->
                <div
                  class="total-row discount-row"
                  v-if="discountRules.length > 0"
                >
                  <span class="row-label">
                    <span class="row-icon">🎯</span>
                    {{ $t("detail.tierDiscount") }}
                    <span class="discount-tip"
                      >({{ $t("detail.subscribe")
                      }}{{ subscribePercent }}%)</span
                    >
                  </span>
                  <span class="row-value discount-value">
                    -${{ currentDiscount }}
                    <span class="discount-info-icon">
                      <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <!-- 浮动提示框 -->
                      <div class="discount-info-popover">
                        <div class="popover-title">
                          {{ $t("detail.discountDetail") }}
                        </div>
                        <div
                          class="popover-item"
                          v-for="rule in discountRules"
                          :key="rule.condition"
                          :class="{
                            active: subscribePercent / 100 >= rule.condition,
                          }"
                        >
                          <span class="popover-range"
                            >{{ $t("detail.subscribe") }} ≥
                            {{ rule.percent }}%</span
                          >
                          <span class="popover-discount"
                            >-{{ rule.discountPercent }}%</span
                          >
                        </div>
                        <div class="popover-current">
                          {{
                            $t("detail.currentSubscribe", {
                              percent: subscribePercent,
                              discount: currentDiscount,
                            })
                          }}
                        </div>
                      </div>
                    </span>
                  </span>
                </div>

                <!-- 分割线 -->
                <div class="total-divider"></div>

                <!-- 总计 -->
                <div class="total-row total-final">
                  <span class="row-label">
                    <span class="row-icon">🏷️</span>
                    {{ $t("detail.total") }}
                  </span>
                  <span class="total-price">${{ grandTotalWithDiscount }}</span>
                </div>
              </div>

              <!-- <div class="total-note">* 实际支付以结算页为准</div> -->
            </div>
            <!-- 显示钱包可用余额 -->
            <div class="total-row wallet-balance-row">
              <span class="row-label">
                <span class="row-icon">💰</span>
                {{ $t("memberBox.availableBalance") }}
              </span>
              <span class="wallet-balance-value">${{ walletBalance }}</span>
            </div>

            <!-- ===== 5. 操作按钮 ===== -->
            <!-- 等待预售（按钮内嵌倒计时） -->
            <div v-if="getServerStatus(server) === STATUS.pending">
              <button
                class="btn btn-rental"
                :class="getServerStatusClass(server)"
                :disabled="true"
              >
                <span class="countdown-btn-time">{{
                  formatCountdown(server.start_time)
                }}</span>
              </button>
            </div>

            <!-- 预售中 -->
            <div v-if="getServerStatus(server) === STATUS.preOrder">
              <button
                class="btn btn-rental"
                :class="getServerStatusClass(server)"
                :disabled="calcValueProgress(server) >= 100"
                @click="handleRental"
              >
                {{ getServerStatusBtn(server) }}
              </button>
            </div>

            <!-- 运行中 -->
            <div v-if="getServerStatus(server) === STATUS.active">
              <button
                class="btn btn-rental"
                :class="getServerStatusClass(server)"
                :disabled="true"
              >
                {{ getServerStatusBtn(server) }}
              </button>
            </div>

            <!-- 已结束 -->
            <div v-if="getServerStatus(server) === STATUS.ended">
              <button
                class="btn btn-rental"
                :class="getServerStatusClass(server)"
                :disabled="true"
              >
                {{ getServerStatusBtn(server) }}
              </button>
            </div>

            <!-- ===== 6. 保障信息 ===== -->
            <div class="panel-guarantees">
              <div class="guarantee-item">
                <span class="guarantee-icon">🛡️</span>
                <span>{{ $t("detail.guaranteeSupport") }}</span>
              </div>
              <div class="guarantee-item">
                <span class="guarantee-icon">🔒</span>
                <span>{{ $t("detail.guaranteeData") }}</span>
              </div>
              <div class="guarantee-item">
                <span class="guarantee-icon">⚡</span>
                <span>99.9% SLA</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 推荐服务器 ===== -->
    <!-- <section class="recommend-section">
      <div class="recommend-container">
        <div class="section-header">
          <span class="section-badge">🔄 {{ $t("detail.youMayLike") }}</span>
          <h2 class="section-title">
            {{ $t("detail.recommend")
            }}<span class="highlight">{{ $t("detail.server") }}</span>
          </h2>
        </div>
        <div class="recommend-grid">
          <div
            class="recommend-card"
            v-for="item in recommendServers"
            :key="item.id"
            @click="goToDetail(item.id)"
          >
            <div class="rec-card-header">
              <h4>{{ item.title }}</h4>
              <span class="rec-spec">{{ ServerSpec[item.spec] }}</span>
            </div>
            <div class="rec-gpu">{{ truncateText(item.gpu, 25) }}</div>
            <div class="rec-value">
              <span class="rec-value-label">{{ $t("detail.value") }}</span>
              <span class="rec-value-number"
                >${{ formatValue(item.value) }}</span
              >
            </div>
            <button class="btn btn-rec">{{ $t("detail.viewDetail") }} →</button>
          </div>
        </div>
      </div>
    </section> -->

    <!-- ===== FAQ ===== -->
    <!-- <section class="faq-section">
      <div class="faq-container">
        <div class="section-header">
          <span class="section-badge">❓ {{ $t("detail.faqBadge") }}</span>
          <h2 class="section-title">
            {{ $t("detail.faqTitle") }}<span class="highlight">FAQ</span>
          </h2>
        </div>
        <div class="faq-list">
          <div
            class="faq-item"
            v-for="(faq, index) in faqs"
            :key="index"
            :class="{ active: faq.open }"
            @click="toggleFaq(index)"
          >
            <div class="faq-question">
              <span class="faq-icon">{{ faq.open ? "−" : "+" }}</span>
              <span>{{ faq.question }}</span>
            </div>
            <div class="faq-answer" v-show="faq.open">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section> -->

    <!-- ===== 收益趋势图大弹窗 ===== -->
    <div
      class="chart-modal-overlay"
      v-if="chartModalVisible"
      @click.self="closeChartModal"
    >
      <div class="chart-modal">
        <div class="chart-modal-header">
          <h3>📈 {{ $t("detail.chartModalTitle") }}</h3>
          <button class="modal-close-btn" @click="closeChartModal">✕</button>
        </div>
        <div class="chart-modal-tabs">
          <button
            class="tab-btn"
            :class="{ active: chartRange === 7 }"
            @click="switchChartRange(7)"
          >
            {{ $t("detail.chart7d") }}
          </button>
          <button
            class="tab-btn"
            :class="{ active: chartRange === 15 }"
            @click="switchChartRange(15)"
          >
            {{ $t("detail.chart15d") }}
          </button>
          <button
            class="tab-btn"
            :class="{ active: chartRange === 30 }"
            @click="switchChartRange(30)"
          >
            {{ $t("detail.chart30d") }}
          </button>
        </div>
        <div class="chart-modal-body">
          <div ref="modalChart" class="modal-chart-container"></div>
        </div>
      </div>
    </div>

    <!-- ===== 交易密码弹窗 ===== -->
    <TradePasswordModal
      :visible.sync="passwordModalVisible"
      :product-info="passwordProductInfo"
      :loading="buyLoading"
      :error="passwordError"
      :title="$t('detail.rentConfirm')"
      :label-text="$t('detail.enterPassword')"
      :placeholder="$t('detail.enterPassword')"
      :confirm-text="$t('detail.confirmRent')"
      :cancel-text="$t('detail.cancel')"
      @confirm="handlePasswordConfirm"
      @cancel="handlePasswordCancel"
      @close="handlePasswordClose"
    />
  </div>
</template>

<script>
import * as echarts from "echarts";
import ServerIconIsometric from "@/components/server/ServerIconIsometric.vue";
import TradePasswordModal from "@/components/toast/TradePasswordModal.vue";
import {
  loadProductLanguage,
  getCurrentLanguage,
  isModuleLoaded,
} from "@/locales";

const STATUS = {
  pending: "pending",
  preOrder: "preOrder",
  active: "active",
  ended: "ended",
};

// const ServerSpec = {
//   0: "入门版",
//   1: "轻量版",
//   2: "性能版",
//   3: "旗舰版",
//   4: "超旗舰",
// };

export default {
  name: "RentalDetailPage",
  components: {
    ServerIconIsometric,
    TradePasswordModal,
  },
  data() {
    return {
      STATUS,
      serverCode: "",
      // ServerSpec,
      passwordModalVisible: false,
      buyLoading: false,
      passwordError: "",
      server: {},
      wallet: {},
      serverIncomeTrend: {
        7: [],
        15: [],
        30: [],
      },
      inputPercent: 1,
      subscribePercent: 1,
      totalValue: 100,
      chartModalVisible: false,
      chartRange: 7,
      chartInstance: null,
      modalChartInstance: null,
      quickPercentages: [1, 10, 50, 100],
      recommendServers: [],
      faqs: [
        {
          question: this.$t("detail.faq1Q"),
          answer: this.$t("detail.faq1A"),
          open: true,
        },
        {
          question: this.$t("detail.faq2Q"),
          answer: this.$t("detail.faq2A"),
          open: false,
        },
        {
          question: this.$t("detail.faq3Q"),
          answer: this.$t("detail.faq3A"),
          open: false,
        },
        {
          question: this.$t("detail.faq4Q"),
          answer: this.$t("detail.faq4A"),
          open: false,
        },
        {
          question: this.$t("detail.faq5Q"),
          answer: this.$t("detail.faq5A"),
          open: false,
        },
      ],
      chartStats: {
        max: 0,
        avg: 0,
        min: 0,
        change: 0,
      },
      stageList: [],
      expandStages: false,
      expandTiers: false,
      pollTimer: null,
      now: this.$dayjs().valueOf(),
      countdownTimer: null,
    };
  },

  computed: {
    stageTotalIncome() {
      if (!this.stageList || this.stageList.length === 0) return "0.00";
      const total = this.stageIncomeList.reduce((sum, stage) => {
        return parseFloat(sum) + parseFloat(stage.income || 0);
      }, 0);
      return total.toFixed(2);
    },
    discountRules() {
      const list = this.server.discount || [];
      return list.map((item) => ({
        condition: parseFloat(item.condition) || 0,
        rate: parseFloat(item.discount) || 0,
        percent: Math.round((parseFloat(item.condition) || 0) * 100),
        discountPercent: Math.round((parseFloat(item.discount) || 0) * 100),
      }));
    },
    currentDiscount() {
      const percent = parseFloat(this.subscribePercent) || 0;
      const ratio = percent / 100;
      let rate = 0;
      for (const rule of this.discountRules) {
        if (ratio >= rule.condition) {
          rate = rule.rate;
        }
      }
      const total = parseFloat(this.totalPrice) || 0;
      return (total * rate).toFixed(2);
    },
    hasDiscount() {
      return parseFloat(this.currentDiscount) > 0;
    },

    grandTotalWithDiscount() {
      const total = parseFloat(this.grandTotal) || 0;
      const discount = parseFloat(this.currentDiscount) || 0;
      const result = total - discount;
      return result > 0 ? result.toFixed(2) : "0.00";
    },
    formattedStart() {
      return this.formatDate(this.server.begin_time);
    },
    formattedEnd() {
      return this.formatDate(this.server.end_time);
    },
    activityDays() {
      if (!this.server.end_time) return 0;
      const end = this.$dayjs(this.server.end_time).valueOf();
      const now = this.$dayjs(this.server.begin_time).valueOf();
      const diff = (end - now) / (1000 * 60 * 60 * 24);
      return Math.max(0, Math.ceil(diff));
    },
    remainingPercent() {
      return ((this.server.surplus - this.server.sale) * 100).toFixed(0);
    },
    totalPrice() {
      const basePrice = this.server.value || 0;
      const shareRatio = this.subscribePercent / 100;
      return (basePrice * shareRatio).toFixed(2);
    },
    serviceFee() {
      const basePrice = parseFloat(this.totalPrice) || 0;
      // if (basePrice > 1000) {
      return (basePrice * (this.server.buy_fee || 0.01)).toFixed(2);
      // }
      // return 0;
    },
    grandTotal() {
      const total = parseFloat(this.totalPrice) || 0;
      const service = parseFloat(this.serviceFee) || 0;
      return (total + service).toFixed(2);
    },
    passwordProductInfo() {
      if (!this.server) return null;
      return {
        img: this.server.country_img,
        name: this.server.title,
        price: this.grandTotalWithDiscount,
      };
    },

    stageIncomeList() {
      const totalAmount = parseFloat(this.totalPrice) || 0;
      return this.stageList.map((stage) => {
        //计算当前时间距离本阶段结束的天数
        // const diff = this.$dayjs(stage.end_time).diff(this.$dayjs(), "day");
        // if (diff <= 0) {
        //   return {
        //     ...stage,
        //     income: 0,
        //     current: 0,
        //   };
        // }
        return {
          ...stage,
          income: (totalAmount * (stage.rate || 0)).toFixed(2),
          // current: 1,
        };
      });
    },
    walletBalance() {
      const val = parseFloat(this.wallet?.balance);
      return isNaN(val) ? "0.00" : val.toFixed(2);
    },
  },

  watch: {
    subscribePercent(newVal) {
      console.log("认购百分比更新:", newVal);
    },
    "$i18n.locale": function (newLang, oldLang) {
      console.log(`语言从 ${oldLang} 切换到 ${newLang}`);
      // 重新加载产品翻译（如果切换到的新语言还没有该产品翻译）
      this.loadProductTranslations();
    },
  },

  async mounted() {
    this.serverCode = this.$route.query.code;
    this.getWallet();
    if (this.serverCode) {
      this.getServer(this.serverCode);
      this.getServerIncome(this.serverCode);
    }
    this.getRecommendList();
    this.$nextTick(() => {
      this.initChart();
    });

    await this.loadProductTranslations();

    // 启动倒计时定时器，每秒更新
    this.countdownTimer = setInterval(() => {
      this.now = this.$dayjs().valueOf();
    }, 1000);
  },
  beforeDestroy() {
    this.stopPolling();
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
    if (this.modalChartInstance) {
      this.modalChartInstance.dispose();
    }
  },
  methods: {
    async loadProductTranslations() {
      const lang = getCurrentLanguage();
      const moduleName = `server/${this.serverCode}`;

      // 检查是否已加载
      if (isModuleLoaded(lang, moduleName)) {
        // console.log(`✅ 产品翻译已存在: ${moduleName}`);
        return;
      }

      try {
        await loadProductLanguage(lang, moduleName);
        // console.log(`✅ 产品 ${moduleName} 翻译加载完成`);
      } catch (error) {
        console.error("产品翻译加载失败:", error);
      }
    },
    getWallet() {
      this.$api.userWallet
        .getWallet()
        .then((res) => {
          this.wallet = res.data || {};
        })
        .catch(() => {
          this.wallet = {};
        });
    },
    getServer(code) {
      this.$api.server.getServer({ code }).then((res) => {
        if (res.code === 1) {
          this.$message?.error(res.msg);
          return;
        }
        this.server = res.data || {};
        this.stageList = this.server.phase || [];
        if (this.server.sale >= this.server.surplus) {
          this.setSubscribePercent(0);
        }
        this.$nextTick(() => {
          this.updateChartData(this.chartRange);
        });
        this.startPolling();
      });
    },
    getServerIncome(code) {
      this.$api.server.getServerIncome({ code }).then((res) => {
        if (res.code === 1) {
          this.$message?.error(res.msg);
          return;
        }
        const trend = res.data || {};
        this.serverIncomeTrend = {
          7: trend[7] || trend["7"] || [],
          15: trend[15] || trend["15"] || [],
          30: trend[30] || trend["30"] || [],
        };
        this.updateChartData(this.chartRange);
        if (this.modalChartInstance) {
          this.updateModalChart(this.chartRange);
        }
        this.$nextTick(() => {
          this.updateChartData(this.chartRange);
        });
      });
    },
    getRecommendList() {
      this.$api.server.getServerList({ page: 1, size: 4 }).then((res) => {
        if (res.code === 0 && res.data) {
          this.recommendServers = res.data.data || [];
        }
      });
    },
    truncateText(text, maxLength) {
      if (!text) return "--";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },
    formatDate(dateStr) {
      if (!dateStr) return "--";
      // 只取日期字符串的 YYYY-MM-DD 部分交给 dayjs，
      // 避免带时区偏移（如 -04:00）的时间被换算成其他时区的日期
      if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        dateStr = dateStr.substring(0, 10);
      }
      return this.$dayjs(dateStr).format("YYYY-MM-DD");
    },
    formatValue(value) {
      if (!value) return "0";
      if (value >= 10000) {
        return (value / 10000).toFixed(1) + this.$t("detail.tenThousand");
      }
      return value.toLocaleString();
    },
    // ===== 格式化倒计时 =====
    formatCountdown(time) {
      if (!time) return "";

      const targetTime = this.$dayjs(time);
      if (!targetTime.isValid()) return "";

      const diff = targetTime.diff(this.$dayjs(this.now));
      if (diff <= 0) return "00:00:00";

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const pad = (n) => String(n).padStart(2, "0");

      if (days > 0) {
        return `${days}${this.$t("serverList.days")} ${pad(hours)}:${pad(
          minutes,
        )}:${pad(seconds)}`;
      }
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    },
    onPercentInput() {
      let val = parseFloat(this.inputPercent);
      if (isNaN(val)) val = 1;
      val = Math.round(val * 100) / 100;
      if (val < 1) {
        val = 1;
        this.inputPercent = 1;
      }
      if (val > this.remainingPercent) {
        this.inputPercent = this.remainingPercent;
        this.subscribePercent = this.remainingPercent;
      } else {
        this.subscribePercent = val;
      }
    },
    onSliderInput(event) {
      const val = parseInt(event.target.value) || 0;
      this.subscribePercent = val;
      this.inputPercent = val;
    },
    setSubscribePercent(value) {
      let val = parseFloat(value);
      if (isNaN(val)) val = 1;
      val = Math.round(val * 100) / 100;
      val = Math.min(this.remainingPercent, Math.max(1, val));
      this.subscribePercent = val;
      this.inputPercent = val;
    },
    initChart() {
      const dom = this.$refs.incomeChart;
      if (!dom) return;
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }
      this.chartInstance = echarts.init(dom);
      this.updateChartData(7);
    },
    generateChartData(days) {
      const trend = this.serverIncomeTrend[days] || [];
      const data = [];
      let rateTotal = 0;
      if (Array.isArray(trend) && trend.length > 0) {
        trend.forEach((item) => {
          const label = item.date ? item.date.slice(5) : "--";
          const value = parseFloat(item.amount) || 0;
          data.push({ label, value });
          rateTotal += parseFloat(item.rate) || 0;
        });
      } else {
        const now = this.$dayjs();
        for (let i = days - 1; i >= 0; i--) {
          const date = now.subtract(i, "day");
          const label =
            days <= 15
              ? `${date.month() + 1}/${date.date()}`
              : `Day ${days - i}`;
          data.push({ label, value: 0 });
        }
      }
      const values = data.map((d) => d.value);
      const total = values.reduce((a, b) => a + b, 0);
      this.chartStats.max = Math.round(Math.max(...values) * 10) / 10;
      this.chartStats.min = Math.round(Math.min(...values) * 10) / 10;
      this.chartStats.avg =
        Math.round((total / (values.length || 1)) * 10) / 10;
      this.chartStats.change = rateTotal.toFixed(2);
      return data;
    },
    updateChartData(days) {
      const data = this.generateChartData(days);
      const option = this.getChartOption(data);
      if (this.chartInstance) {
        this.chartInstance.setOption(option, true);
        this.chartInstance.resize();
      }
    },
    getChartOption(data) {
      return {
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            const p = params[0];
            return `<div style="font-size:13px;font-weight:600;">${p.axisValue}</div>
                    <div style="font-size:15px;color:#00b4ff;font-weight:700;">$${p.value}</div>`;
          },
          backgroundColor: "rgba(10, 14, 26, 0.92)",
          borderColor: "rgba(0, 180, 255, 0.15)",
          borderWidth: 1,
          textStyle: { color: "#fff", fontSize: 12 },
          padding: [10, 14],
        },
        grid: {
          left: -15,
          right: -15,
          top: 10,
          bottom: 8,
        },
        xAxis: {
          type: "category",
          data: data.map((d) => d.label),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: "rgba(255,255,255,0.2)",
            fontSize: 10,
            interval: Math.floor(data.length / 8),
          },
        },
        yAxis: {
          type: "value",
          splitLine: { show: false },
          axisLabel: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        series: [
          {
            type: "line",
            smooth: true,
            data: data.map((d) => d.value),
            symbol: "circle",
            symbolSize: 3,
            lineStyle: {
              color: "#00b4ff",
              width: 2,
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "rgba(0, 180, 255, 0.35)" },
                { offset: 0.6, color: "rgba(0, 100, 255, 0.1)" },
                { offset: 1, color: "rgba(0, 180, 255, 0.02)" },
              ]),
            },
            itemStyle: {
              color: "#00b4ff",
            },
          },
        ],
      };
    },
    openChartModal() {
      this.chartModalVisible = true;
      this.$nextTick(() => {
        this.initModalChart();
      });
    },
    closeChartModal() {
      this.chartModalVisible = false;
      if (this.modalChartInstance) {
        this.modalChartInstance.dispose();
        this.modalChartInstance = null;
      }
    },
    initModalChart() {
      const dom = this.$refs.modalChart;
      if (!dom) return;
      if (this.modalChartInstance) {
        this.modalChartInstance.dispose();
      }
      this.modalChartInstance = echarts.init(dom);
      this.updateModalChart(this.chartRange);
    },
    updateModalChart(days) {
      const data = this.generateChartData(days);
      const option = this.getModalChartOption(data);
      if (this.modalChartInstance) {
        this.modalChartInstance.setOption(option, true);
        this.modalChartInstance.resize();
      }
    },
    getModalChartOption(data) {
      return {
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            const p = params[0];
            return `<div style="font-size:13px;font-weight:600;">${p.axisValue}</div>
                    <div style="font-size:15px;color:#00b4ff;font-weight:700;">$${p.value}</div>`;
          },
          backgroundColor: "rgba(10, 14, 26, 0.92)",
          borderColor: "rgba(0, 180, 255, 0.15)",
          borderWidth: 1,
          textStyle: { color: "#fff", fontSize: 12 },
          padding: [10, 14],
        },
        grid: {
          left: 40,
          right: 20,
          top: 30,
          bottom: 30,
        },
        xAxis: {
          type: "category",
          data: data.map((d) => d.label),
          axisLine: { lineStyle: { color: "rgba(255,255,255,0.15)" } },
          axisTick: { show: false },
          axisLabel: {
            color: "rgba(255,255,255,0.5)",
            fontSize: 12,
            interval: Math.floor(data.length / 10),
          },
        },
        yAxis: {
          type: "value",
          splitLine: { lineStyle: { color: "rgba(255,255,255,0.08)" } },
          axisLabel: {
            color: "rgba(255,255,255,0.5)",
            fontSize: 12,
            formatter: (v) => `$${v}`,
          },
        },
        series: [
          {
            type: "line",
            smooth: true,
            data: data.map((d) => d.value),
            symbol: "circle",
            symbolSize: 5,
            lineStyle: {
              color: "#00b4ff",
              width: 3,
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "rgba(0, 180, 255, 0.4)" },
                { offset: 0.6, color: "rgba(0, 100, 255, 0.12)" },
                { offset: 1, color: "rgba(0, 180, 255, 0.02)" },
              ]),
            },
            itemStyle: {
              color: "#00b4ff",
            },
          },
        ],
      };
    },
    switchChartRange(days) {
      this.chartRange = days;
      this.updateChartData(days);
      if (this.modalChartInstance) {
        this.updateModalChart(days);
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => {
        this.pollIncome();
      }, 30000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    pollIncome() {
      if (!this.serverCode) return;
      this.$api.server.getServer({ code: this.serverCode }).then((res) => {
        if (res.code === 0 && res.data) {
          this.server = res.data;
          this.stageList = this.server.phase || [];
          this.updateChartData(this.chartRange);
        }
      });
      this.$api.server
        .getServerIncome({ code: this.serverCode })
        .then((res) => {
          if (res.code === 0 && res.data) {
            const trend = res.data || {};
            this.serverIncomeTrend = {
              7: trend[7] || trend["7"] || [],
              15: trend[15] || trend["15"] || [],
              30: trend[30] || trend["30"] || [],
            };
            this.updateChartData(this.chartRange);
            if (this.modalChartInstance) {
              this.updateModalChart(this.chartRange);
            }
          }
        });
    },
    toggleFaq(index) {
      this.faqs[index].open = !this.faqs[index].open;
    },

    // 按美国东部时区解析服务器时间
    // 无时区的 "MM/DD/YYYY HH:mm" / "YYYY-MM-DD HH:mm:ss" → 转 ISO 后按 America/New_York 解析（自动处理夏令时）
    parseServerTime(str) {
      if (!str) return null;
      const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}:\d{2})/);
      if (m) {
        const iso = `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(
          2,
          "0",
        )}T${m[4]}:00`;
        return this.$dayjs.tz(iso, "America/New_York");
      }
      if (
        /^\d{4}-\d{2}-\d{2}/.test(str) &&
        !/[Zz]|[+-]\d{2}:\d{2}$/.test(str)
      ) {
        return this.$dayjs.tz(str, "America/New_York");
      }
      // 带时区偏移（如 -04:00）/ 时间戳 → 按绝对时刻解析
      return this.$dayjs(str);
    },
    //状态
    isServerActive(server) {
      return this.getServerStatus(server) !== STATUS.pending;
    },
    // ===== 判断服务器状态 =====
    getServerStatus(server) {
      if (!server) return STATUS.ended;
      const now = this.$dayjs();
      // 服务器时间统一按美国东部时区（America/New_York，自动处理夏令时）解析，
      // 不随浏览器本地时区变化，保证各浏览器判断结果一致
      const startTime = server.start_time
        ? this.$dayjs(server.start_time)
        : null;
      const beginTime = server.begin_time
        ? this.parseServerTime(server.begin_time)
        : null;
      const endTime = server.end_time
        ? this.parseServerTime(server.end_time)
        : null;

      if (!startTime && !beginTime) {
        return server.status == 2 ? STATUS.active : STATUS.ended;
      }

      if (startTime && now.isBefore(startTime)) {
        return STATUS.pending;
      }

      if (beginTime && now.isBefore(beginTime)) {
        return STATUS.preOrder;
      }

      if (endTime && now.isAfter(endTime)) {
        return STATUS.ended;
      }

      return STATUS.active;
    },
    //返回class
    getServerStatusClass(server) {
      if (!server) return "";
      // 等待预购中
      if (this.getServerStatus(server) === STATUS.pending) {
        return STATUS.pending;
      }

      // 预购中
      if (
        this.getServerStatus(server) === STATUS.preOrder &&
        this.calcValueProgress(server) < 100
      ) {
        return STATUS.preOrder;
      }

      // 运行中
      if (this.getServerStatus(server) === STATUS.active) {
        return STATUS.active;
      }

      return STATUS.ended;
    },
    //返回状态文字
    getServerStatusText(server) {
      //已结束
      if (this.getServerStatus(server) === STATUS.ended) {
        return this.$t("serverList.btnEnded");
      }

      //等待预购中
      if (this.getServerStatus(server) === STATUS.pending) {
        return this.$t("serverList.btnWait");
      }

      //运行中
      if (this.getServerStatus(server) === STATUS.active) {
        return this.$t("serverList.yunxz");
      }

      //已满额
      if (this.calcValueProgress(server) >= 100) {
        return this.$t("serverList.full");
      }

      //预购中
      if (this.getServerStatus(server) === STATUS.preOrder) {
        return this.$t("serverList.available");
      }

      //已满额
      return this.$t("serverList.full");
    },

    //返回状态文字
    getServerStatusBtn(server) {
      //已结束
      if (this.getServerStatus(server) === STATUS.ended) {
        return this.$t("serverList.btnEnded");
      }

      //等待预购中
      if (this.getServerStatus(server) === STATUS.pending) {
        return this.$t("serverList.look");
      }

      //运行中
      if (this.getServerStatus(server) === STATUS.active) {
        return this.$t("serverList.yunxz");
      }

      //已满额
      if (this.calcValueProgress(server) >= 100) {
        return this.$t("serverList.full");
      }

      //预购中
      if (this.getServerStatus(server) === STATUS.preOrder) {
        return this.$t("serverList.btnRent");
      }

      //已满额
      return this.$t("serverList.full");
    },
    calcValueProgress(item) {
      const rate = (1 - (item.surplus - item.sale)) * 100;
      if (!rate) return 0;
      return Math.min(Math.round(rate), 100);
    },
    handleRental() {
      if (
        (this.getServerStatus(this.server) !== "active" &&
          this.getServerStatus(this.server) !== "preOrder") ||
        this.calcValueProgress(this.server) >= 100
      ) {
        this.$message?.warning(this.$t("detail.outOfStockMsg"));
        return;
      }
      if (this.subscribePercent <= 0) {
        this.$message?.warning(this.$t("detail.selectShareMsg"));
        return;
      }

      if (this.subscribePercent > this.remainingPercent) {
        this.$message?.warning(
          this.$t("detail.shareMsg", { n: this.remainingPercent }),
        );
        return;
      }

      this.passwordModalVisible = true;
      this.passwordError = "";
    },
    handlePasswordConfirm(password) {
      this.buyLoading = true;
      this.passwordError = "";
      const params = {
        code: this.server.code,
        share: this.subscribePercent,
        trade_password: password,
      };
      this.$api.server
        .buyServer(params)
        .then((res) => {
          if (res.code === 1) {
            this.passwordError = res.msg || this.$t("detail.rentFail");
            return;
          }
          this.$message?.success(this.$t("detail.rentSuccess"));
          this.passwordModalVisible = false;
          // this.$router.push("/my");
          this.getWallet();
        })
        .finally(() => {
          this.buyLoading = false;
        });
    },
    handlePasswordCancel() {
      this.passwordModalVisible = false;
    },
    handlePasswordClose() {
      this.passwordModalVisible = false;
    },
    handleContact() {
      this.$message?.info(this.$t("detail.contactService"));
    },
    goToDetail(id) {
      this.$router.push({ path: "/rental/detail", query: { id } });
    },
  },
};
</script>

<style scoped>
.rental-detail-page {
  min-height: 100vh;
  background: #0a0e1a;
  color: #e8eaf0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}

/* ===== 面包屑 ===== */
.breadcrumb {
  background: #0d1220;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 14px 0;
}

.breadcrumb-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
}

.breadcrumb-container a {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-container a:hover {
  color: #00b4ff;
}

.breadcrumb-sep {
  margin: 0 8px;
  color: rgba(255, 255, 255, 0.3);
}

.breadcrumb-current {
  color: #00b4ff;
}

/* ===== 主内容 ===== */
.detail-main {
  padding: 24px 0 40px;
}

.detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 头部 */
.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.detail-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-title {
  font-size: 26px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(90deg, #fff, #a0d8ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.detail-spec {
  font-size: 13px;
  color: #00b4ff;
  background: rgba(0, 180, 255, 0.12);
  border: 1px solid rgba(0, 180, 255, 0.3);
  padding: 3px 10px;
  border-radius: 20px;
}

.detail-badges {
  display: flex;
  gap: 8px;
}

.badge {
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 500;
}

.badge-hot {
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.badge-available {
  background: rgba(46, 213, 115, 0.15);
  color: #2ed573;
  border: 1px solid rgba(46, 213, 115, 0.3);
}

.badge-sold {
  background: rgba(255, 159, 67, 0.15);
  color: #ff9f43;
  border: 1px solid rgba(255, 159, 67, 0.3);
}

/* 等待预购中 */
.badge.pending {
  background: rgba(185, 185, 185, 0.12);
  color: #b9b9b9;
  border: 1px solid rgba(185, 185, 185, 0.2);
}

.badge.preOrder {
  background: rgba(0, 201, 1, 0.12);
  color: #00c951;
  border-color: rgba(0, 201, 1, 0.2);
}

.badge.active {
  background: rgba(0, 201, 1, 0.12);
  color: #00c951;
  border-color: rgba(0, 201, 1, 0.2);
}

.badge.ended {
  background: rgba(255, 87, 34, 0.12);
  color: #ff5722;
  border-color: rgba(255, 87, 34, 0.2);
}

/* 等待预购中 */
.spec-value.pending {
  background: rgba(126, 126, 126, 0.12);
  color: #b9b9b9;
  border-color: rgba(126, 126, 126, 0.2);
  padding: 2px 10px 3px;
  border-radius: 15px;
}

.spec-value.preOrder {
  background: rgba(0, 201, 1, 0.12);
  color: #00c951;
  border-color: rgba(0, 201, 1, 0.2);
  padding: 2px 10px 3px;
  border-radius: 15px;
}

.spec-value.active {
  background: rgba(0, 201, 1, 0.12);
  color: #00c951;
  border-color: rgba(0, 201, 1, 0.2);
  padding: 2px 10px 3px;
  border-radius: 15px;
}

.spec-value.ended {
  background: rgba(255, 87, 34, 0.12);
  color: #ff5722;
  border-color: rgba(255, 87, 34, 0.2);
  padding: 2px 10px 3px;
  border-radius: 15px;
}

.badge-wks {
  background: #0068ff26;
  color: #0068ff;
  border: 1px solid #0068ff4d;
}

.xxqq {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
}

/* 左右布局 */
.detail-container {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.detail-left {
  flex: 1;
  min-width: 0;
}

.detail-right {
  width: 380px;
  flex-shrink: 0;
}

/* GPU 卡片 */
.detail-gpu-card {
  display: flex;
  align-items: center;
  gap: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 0px 10px;
  margin-bottom: 20px;
}

.gpu-icon {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  background: rgba(0, 180, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00b4ff;
  flex-shrink: 0;
}

.gpu-model {
  font-size: 18px;
  font-weight: 700;
}

.gpu-details {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.gpu-detail-item {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.gpu-dot {
  margin: 0 8px;
}

/* ===== 规格参数 + 趋势图包装 ===== */
.detail-specs-wrapper {
  display: flex;
  gap: 20px;
  margin-bottom: 28px;
}

/* ===== 规格参数卡片 ===== */
.detail-specs-card {
  flex: 1;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 24px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.card-icon {
  font-size: 20px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.specs-grid {
  display: grid;
  gap: 6px 24px;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.spec-item:last-child {
  border-bottom: none;
}

.spec-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.spec-value {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  text-align: right;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  /* white-space: nowrap; */
  display: flex;
  gap: 10px;
}

/* ===== 收益趋势图卡片 ===== */
.detail-chart-card {
  flex: 0 0 280px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 24px;
}

.chart-badge {
  font-size: 10px;
  color: #00c951;
  background: rgba(0, 201, 81, 0.1);
  padding: 2px 12px;
  border-radius: 12px;
  margin-left: auto;
  font-weight: 600;
}

.chart-preview {
  position: relative;
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.chart-preview:hover {
  background: rgba(255, 255, 255, 0.02);
}

.chart-container {
  width: 100%;
  height: 150px;
}

.chart-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.15);
  padding: 4px 0 2px;
  transition: color 0.3s;
}

.chart-preview:hover .chart-hint {
  color: rgba(0, 180, 255, 0.4);
}

.chart-hint svg {
  opacity: 0.5;
}

/* ===== 图表筛选按钮 ===== */
.chart-filter {
  display: flex;
  gap: 6px;
  margin-top: 10px;
  justify-content: center;
}

.filter-btn {
  padding: 4px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.filter-btn:hover {
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.6);
}

.filter-btn.active {
  background: rgba(0, 180, 255, 0.08);
  border-color: rgba(0, 180, 255, 0.2);
  color: #00b4ff;
}

/* ===== 图表统计摘要 ===== */
.chart-stats {
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin-top: 50px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.03);
}

.chart-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
}

.stat-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 15px;
  font-weight: 700;
}

.stat-high {
  color: #00c951;
}

.stat-avg {
  color: #ffd866;
}

.stat-low {
  color: #ff6b6b;
}

.stat-change {
  color: #00b4ff;
}

.chart-stat-divider {
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.04);
}

/* ===== 描述 ===== */
.detail-description {
  margin-bottom: 20px;
}

.desc-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.desc-icon {
  font-size: 18px;
}

.desc-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.detail-description p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.8;
  margin: 0;
}

/* ===== 国家/地区 ===== */
.detail-scenarios {
  margin-bottom: 0;
}

.scenarios-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.scenarios-icon {
  font-size: 18px;
}

.scenarios-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.scenarios-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.scenario-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 5px 12px;
  border-radius: 20px;
}

.country-flag-small {
  width: 18px;
  height: 12px;
  border-radius: 2px;
  object-fit: cover;
}

/* ===== 右侧租赁面板 ===== */
.rental-panel {
  background: linear-gradient(180deg, #111827, #0d1220);
  border: 1px solid rgba(0, 180, 255, 0.15);
  border-radius: 16px;
  padding: 20px;
  position: sticky;
  top: 20px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.panel-title {
  font-size: 17px;
  font-weight: 700;
  color: #fff;
}

.panel-status {
  font-size: 12px;
  font-weight: 500;
}

.status-on {
  color: #2ed573;
}

.status-off {
  color: #ff9f43;
}

.panel-group {
  margin-bottom: 16px;
}

.panel-label {
  display: block;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 8px;
}

/* 活动周期 */
.activity-period-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 8px 12px;
}

.period-date-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.period-display-value {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}

.period-display-arrow {
  color: #00b4ff;
  font-size: 13px;
}

.period-display-days {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.days-number {
  font-size: 18px;
  font-weight: 700;
  color: #00b4ff;
}

.days-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

/* 滑条 */
.slider-wrapper {
  padding: 4px 0;
}

.subscribe-slider {
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(
    90deg,
    #00b4ff 0%,
    #00b4ff var(--fill, 0%),
    rgba(255, 255, 255, 0.1) var(--fill, 0%)
  );
  outline: none;
}

.subscribe-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #00b4ff;
  border: 3px solid #fff;
  cursor: pointer;
  box-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 6px;
}

.current-value {
  color: #00b4ff;
  font-weight: 600;
}

/* 百分比输入框 */
.percent-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 8px 12px;
}

.percent-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
}

.percent-input::-webkit-outer-spin-button,
.percent-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.percent-unit {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
}

.remaining-share {
  font-size: 12px;
  color: #ffc107;
  background: rgba(255, 193, 7, 0.12);
  border: 1px solid rgba(255, 193, 7, 0.2);
  border-radius: 6px;
  padding: 2px 10px;
  display: inline-block;
  margin-bottom: 8px;
  font-weight: 500;
}

/* 面板总计 */
.panel-total {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 16px;
}

.total-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.total-title {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
}

.total-badge {
  font-size: 11px;
  color: #ffd866;
  background: rgba(255, 216, 102, 0.12);
  padding: 2px 8px;
  border-radius: 10px;
}

.total-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
}

.row-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.7);
}

.row-icon {
  font-size: 13px;
}

.row-value {
  color: #e8eaf0;
  font-weight: 500;
}

.service-value {
  color: #ff9f43;
}

.discount-value {
  color: #2ed573;
}

.total-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 4px 0;
}

.total-final {
  font-weight: 600;
}

.total-price {
  font-size: 18px;
  font-weight: 700;
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.total-price.income {
  background: linear-gradient(135deg, #ff6b6b, #ff6b6b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* 钱包可用余额 */
.wallet-balance-row {
  margin: 10px 0;
  padding: 8px 10px;
  background: rgba(0, 180, 255, 0.06);
  border: 1px dashed rgba(0, 180, 255, 0.25);
  border-radius: 8px;
}

.wallet-balance-value {
  font-size: 16px;
  font-weight: 700;
  color: #4fc3ff;
}

.total-note {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 8px;
}

/* 优惠提示 */
.discount-tip {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}

.discount-info-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
  margin-left: 4px;
  vertical-align: middle;
}

.discount-info-icon:hover {
  color: #00b4ff;
}

.discount-info-popover {
  position: absolute;
  right: 0;
  top: 22px;
  width: 220px;
  background: #1a2233;
  border: 1px solid rgba(0, 180, 255, 0.2);
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  z-index: 100;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: all 0.2s;
}

.discount-info-icon:hover .discount-info-popover {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.popover-title {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
}

.popover-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  padding: 4px 0;
}

.popover-item.active {
  color: #00b4ff;
  font-weight: 600;
}

.popover-discount {
  color: #2ed573;
}

.popover-current {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 12px;
  color: #ffd866;
}

/* 预估收益明细 - 阶段时间线 */
.stage-wrapper {
  position: relative;
}

.stage-timeline {
  position: relative;
}

.stage-item {
  transition: opacity 0.2s;
}

.stage-item.hidden {
  display: none;
}

.stage-date {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  margin-left: 4px;
}

.stage-income {
  color: #ffd866;
  font-weight: 600;
}

.stage-more-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  padding: 4px 0;
}

.more-dots {
  letter-spacing: 2px;
  color: #ffd866;
}

.more-text {
  color: rgba(255, 255, 255, 0.5);
}

.more-arrow {
  margin-left: auto;
  font-size: 11px;
  color: #ffd866;
  /* opacity: 0.7; */
}

/* 按钮（与 home/ServerList.vue .btn-rental 保持一致） */
.btn {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 10px;
  font-family: inherit;
  white-space: nowrap;
}

.btn-rental-blue {
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  box-shadow: 0 4px 20px rgba(255, 152, 0, 0.2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: not-allowed;
}

.btn-rental-now {
  background: linear-gradient(135deg, #ff9800, #ff6d00);
  box-shadow: 0 4px 20px rgba(255, 152, 0, 0.2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-rental-now:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 100, 255, 0.3);
}

.btn-rental-now:disabled {
  background: linear-gradient(135deg, #00c951, #00a843);
  box-shadow: 0 4px 20px rgba(0, 201, 81, 0.2);
  color: #fff;
  cursor: not-allowed;
}

.btn-contact {
  background: rgba(255, 255, 255, 0.06);
  color: #e8eaf0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-contact:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* 等待预售倒计时 */
.countdown-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 10px;
  background: rgba(0, 180, 255, 0.08);
  border: 1px solid rgba(0, 180, 255, 0.15);
}

.countdown-label {
  font-size: 12px;
  color: rgba(0, 180, 255, 0.7);
  font-weight: 500;
  white-space: nowrap;
}

.countdown-time {
  font-size: 15px;
  font-weight: 800;
  color: #00b4ff;
  font-family: "SF Mono", "Courier New", monospace;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* 保障 */
.panel-guarantees {
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.guarantee-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}

.guarantee-icon {
  font-size: 13px;
}

/* ===== 推荐服务器 ===== */
.recommend-section {
  padding: 60px 20px 80px;
  background: rgba(255, 255, 255, 0.01);
  border-top: 1px solid rgba(255, 255, 255, 0.03);
}

.recommend-container {
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.section-badge {
  display: inline-block;
  padding: 4px 16px;
  border-radius: 20px;
  background: rgba(0, 180, 255, 0.08);
  border: 1px solid rgba(0, 180, 255, 0.1);
  font-size: 13px;
  color: #00b4ff;
  margin-bottom: 12px;
}

.section-title {
  font-size: 36px;
  font-weight: 800;
  margin: 0;
}

.highlight {
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.recommend-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.recommend-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.recommend-card:hover {
  transform: translateY(-4px);
  border-color: rgba(0, 180, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
}

.rec-card-header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.rec-card-header h4 {
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}

.rec-spec {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.04);
  padding: 2px 10px;
  border-radius: 10px;
}

.rec-gpu {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  margin: 8px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rec-value {
  margin: 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.rec-value-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.2);
}

.rec-value-number {
  font-size: 18px;
  font-weight: 700;
  color: #ffd866;
}

.btn-rec {
  padding: 6px 20px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-rec:hover {
  background: rgba(0, 180, 255, 0.08);
  border-color: rgba(0, 180, 255, 0.15);
  color: #00b4ff;
}

/* ============================================================
   FAQ
   ============================================================ */
.faq-section {
  padding: 60px 20px 80px;
  background: #0a0e1a;
}

.faq-container {
  max-width: 800px;
  margin: 0 auto;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.faq-item {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.faq-item:hover {
  border-color: rgba(255, 255, 255, 0.08);
}

.faq-item.active {
  border-color: rgba(0, 180, 255, 0.1);
}

.faq-question {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.faq-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  font-size: 16px;
  color: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.faq-item.active .faq-icon {
  background: rgba(0, 180, 255, 0.1);
  color: #00b4ff;
}

.faq-answer {
  padding: 0 20px 20px 58px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.8;
}

.faq-answer p {
  margin: 0;
}

/* ===== 图表弹窗 ===== */
.chart-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-modal {
  width: 720px;
  max-width: 90vw;
  background: #111827;
  border: 1px solid rgba(0, 180, 255, 0.2);
  border-radius: 16px;
  padding: 20px;
}

.chart-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.chart-modal-header h3 {
  font-size: 16px;
  color: #fff;
  margin: 0;
}

.modal-close-btn {
  background: rgba(255, 255, 255, 0.08);
  border: none;
  color: rgba(255, 255, 255, 0.6);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.chart-modal-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.tab-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  border-color: rgba(0, 180, 255, 0.4);
  color: #00b4ff;
}

.tab-btn.active {
  background: rgba(0, 180, 255, 0.15);
  border-color: rgba(0, 180, 255, 0.5);
  color: #00b4ff;
}

.chart-modal-body {
  height: 400px;
}

.modal-chart-container {
  width: 100%;
  height: 100%;
}

/* ===== 响应式 ===== */
@media (max-width: 1024px) {
  .detail-container {
    flex-direction: column;
  }

  .detail-right {
    width: 100%;
  }

  .rental-panel {
    position: static;
  }

  .detail-specs-wrapper {
    flex-direction: column;
  }

  .detail-chart-card {
    flex: unset;
  }

  .specs-grid {
    gap: 4px 12px;
  }

  .modal-chart-container {
    height: 260px;
  }

  .chart-container {
    height: 100px;
  }

  .detail-gpu-card {
    flex-wrap: wrap;
  }

  .chart-stats {
    flex-wrap: wrap;
  }

  .recommend-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .detail-title {
    font-size: 20px;
  }

  .detail-gpu-card {
    flex-direction: column;
  }

  .modal-chart-container {
    height: 200px;
  }

  .spec-item {
    flex-wrap: wrap;
  }

  .chart-stats {
    gap: 2px;
  }

  .recommend-grid {
    grid-template-columns: 1fr;
  }

  .chart-modal {
    padding: 14px;
  }

  .chart-modal-body {
    height: 300px;
  }

  .panel-guarantees {
    flex-direction: column;
    gap: 8px;
  }
}

.xxb {
  color: #ffc107;
  font-size: 15px;
  cursor: pointer;
}

.xxb:hover {
  color: rgba(255, 255, 255, 0.6);
}

/* ============================================================
   科技蓝统一配色（覆盖重载）
   - 去除过亮的橙/绿/黄，统一为冷色科技蓝
   ============================================================ */

/* ---------- 徽章：红/绿/橙 → 品牌蓝 ---------- */

.badge-available {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
  border-color: rgba(0, 180, 255, 0.2);
}

.badge-sold {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
  border-color: rgba(0, 180, 255, 0.2);
}

/* ---------- pending 等待按钮 → 品牌蓝（与 active 统一） ---------- */
.btn-rental-blue {
  background: linear-gradient(135deg, #00b4ff, #0068ff);
  box-shadow: 0 4px 20px rgba(0, 100, 255, 0.2);
  cursor: not-allowed;
}

/* ---------- 主操作按钮：橙色 → 品牌蓝 ---------- */
.btn-rental-now {
  background: linear-gradient(135deg, #00b4ff, #0068ff);
  box-shadow: 0 4px 20px rgba(0, 100, 255, 0.2);
}

/* 运行中/已结束按钮：绿色 → 品牌蓝 */
.btn-rental-now:disabled {
  background: linear-gradient(135deg, #00b4ff, #0068ff);
  box-shadow: 0 4px 20px rgba(0, 100, 255, 0.15);
}

/* ==================== pending = 灰色 ==================== */
/* pending 徽章：蓝色 → 灰色 */
.badge-wks {
  background: rgba(148, 163, 184, 0.12);
  color: #94a3b8;
  border-color: rgba(148, 163, 184, 0.22);
}

/* pending 按钮 */
.btn-rental.pending {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  box-shadow: 0 4px 20px rgba(148, 163, 184, 0.15);
  cursor: not-allowed;
  color: #fff;
}

/*预购中*/
.btn-rental.preOrder {
  background: linear-gradient(135deg, #00b4ff, #0068ff);
  box-shadow: 0 4px 20px rgba(0, 100, 255, 0.2);
  cursor: pointer;
  color: #fff;
}

/*运行中*/
.btn-rental.active {
  background: linear-gradient(135deg, #00b4ff85, #0068ffa8);
  box-shadow: 0 4px 20px rgba(0, 100, 255, 0.2);
  cursor: not-allowed;
  color: #fff;
}

/*已结束*/
.btn-rental.ended {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  box-shadow: 0 4px 20px rgba(148, 163, 184, 0.15);
  cursor: not-allowed;
  color: #fff;
}

/* pending 等待倒计时按钮 → 灰色 */
.btn-rental-blue.countdown-btn {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  box-shadow: 0 4px 20px rgba(148, 163, 184, 0.15);
  cursor: not-allowed;
}

/* pending 倒计时文字 → 灰色 */
.btn-rental-blue.countdown-btn .countdown-btn-label,
.btn-rental-blue.countdown-btn .countdown-btn-time {
  color: #d9d9d9;
}
</style>
